import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SettingsIcon from '@mui/icons-material/Settings';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { Grid, IconButton, lighten, Stack, Typography } from '@mui/material';
import { Edge, Node } from 'reactflow';
import { ProductGroups } from 'enums';
import { Funnel, FunnelContact, FunnelTypes, NodeVariants } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import getLocalizedText from 'features/co-pilot/translationFileProduct';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { CampaignBuilderSteps, type Offer } from '../../campaign-builder.types';
import { coerceOfferFieldToString } from '../../offer-field.utils';
import SimpleNodeItem from './simple-node-item';

const SimpleContact: React.FC<{ contact: FunnelContact }> = ({ contact }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const contactName =
    contact.fullName?.trim() || (contact.firstName && contact.lastName ? `${contact.firstName} ${contact.lastName}` : undefined) || t('linkedinUser');

  return (
    <Stack className={classes.simpleContact} direction={'row'} gap={2}>
      <Typography className={classes.contactName}>{contactName}</Typography>
      {!_.isNil(contact.occupation) ? <Typography className={classes.contactOccupation}>{`(${contact.occupation})`}</Typography> : null}
    </Stack>
  );
};

interface ItemProps {
  stepNumber: number;
  icon: React.ReactNode;
  title: string;
  disabled?: boolean;
  children?: React.ReactNode;
  hasError?: boolean;

  selectCampaignStep(): void;
}

const SummaryItem: React.FC<ItemProps> = ({ stepNumber, icon, title, disabled, children, selectCampaignStep, hasError }) => {
  const { classes, cx } = useStyles();

  return (
    <div className={cx(classes.summaryItem, hasError && 'error')}>
      <div className={cx(classes.itemHeader, hasError && 'error')}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Stack className={cx(classes.itemHeaderContent, hasError && 'error')} direction={'row'} alignItems={'center'} gap={2}>
            {icon}
            <Typography className={classes.boldText}>
              {stepNumber}. {title}
            </Typography>
          </Stack>
          <IconButton className={classes.editButton} onClick={selectCampaignStep} disabled={disabled}>
            <EditIcon className={classes.editIcon} />
          </IconButton>
        </Stack>
      </div>
      <div className={classes.itemBody}>{children}</div>
    </div>
  );
};

interface Props {
  campaign: Funnel | null;
  productGroup: ProductGroups;
  isCampaignRunning: boolean;
  finalContacts: FunnelContact[];
  totalFinalContacts: number;
  fullLinkedinAccount: LinkedinAccount | undefined;
  linkedinAccountLoading?: boolean;

  selectCampaignStep(idx: number): () => void;

  handleOpenStopRunningCampaignDialog(campaignBuilderStep: CampaignBuilderSteps): () => void;
}

const CampaignSummary: React.FC<Props> = ({
  campaign,
  productGroup,
  isCampaignRunning,
  finalContacts,
  totalFinalContacts,
  fullLinkedinAccount,
  linkedinAccountLoading = false,
  selectCampaignStep,
  handleOpenStopRunningCampaignDialog,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const { type, reactFlow, linkedinAccount, offer } = campaign || {};
  // Get work time from the LinkedIn account, not the funnel
  const workDays = useMemo(() => fullLinkedinAccount?.workDays || [], [fullLinkedinAccount]);
  const workHours = useMemo(() => fullLinkedinAccount?.workHours || [], [fullLinkedinAccount]);
  const timeZone = useMemo(() => fullLinkedinAccount?.timeZone || '', [fullLinkedinAccount]);

  const activeContacts = finalContacts || [];
  const activeContactsSlice = _.take(activeContacts, 5);
  const activeContactsRest = totalFinalContacts - activeContactsSlice.length;

  const nodes = reactFlow?.nodes || [];
  const nodesWithoutWaitNodes = nodes.filter((node) => node.type !== NodeVariants.WAIT_NODE);
  const nodesSlice = _.take(nodesWithoutWaitNodes, 5);
  const nodesRest = nodesWithoutWaitNodes.length - nodesSlice.length;

  const getAiBriefingSummary = () => {
    const jobOffer = coerceOfferFieldToString(offer?.jobOffer);
    const problems = coerceOfferFieldToString(offer?.problems);
    const features = coerceOfferFieldToString(offer?.features);
    const benefits = coerceOfferFieldToString(offer?.benefits);
    const impact = coerceOfferFieldToString(offer?.impact);

    const placeholdersSummary =
      offer?.customPlaceholders && !_.isEmpty(offer.customPlaceholders)
        ? Object.entries(offer.customPlaceholders)
          .map(([k, v]) => `{{${k}}}: ${v}`)
          .join('\n')
        : '';

    switch (type) {
      case FunnelTypes.ACTIVE_SOURCING_CO_PILOT:
        return [jobOffer, placeholdersSummary].filter(Boolean).join('\n\n');
      case FunnelTypes.SOCIAL_SELLING_CO_PILOT:
        return [`${problems}\n\n${features}\n\n${benefits}\n\n${impact}`, placeholdersSummary].filter(Boolean).join('\n\n');
      default:
        return placeholdersSummary;
    }
  };

  const offerSet = (() => {
    if (_.isNil(offer)) return false;

    // Determine which fields are actually mandatory for the current funnel type
    const requiredFields: (keyof Offer)[] =
      type === FunnelTypes.ACTIVE_SOURCING_CO_PILOT
        ? ['jobOffer']
        : type === FunnelTypes.SOCIAL_SELLING_CO_PILOT
          ? ['problems', 'features', 'benefits', 'impact']
          : [];

    const isFieldFilled = (field: any): boolean => {
      if (_.isNil(field)) return false;
      if (typeof field === 'string') {
        return field.trim() !== '';
      }
      if (typeof field === 'object') {
        // Respect optional usage toggle
        if ('inUse' in field) {
          if (!field.inUse) return true; // not used => ignore
          const val = 'value' in field ? (field.value as string) : '';
          return val.trim() !== '';
        }
        if ('value' in field) {
          const val = (field as any).value as string;
          return val.trim() !== '';
        }
      }
      return false;
    };

    // Only evaluate the required fields for the current funnel
    const allRequiredFilled = requiredFields.every((k) => isFieldFilled((offer as any)[k]));

    // If no required fields defined (other funnel types) fall back to legacy behaviour
    if (requiredFields.length === 0) {
      const { customPlaceholders, ...rest } = offer as any;
      const baseFieldsFilled = _.every(Object.values(rest), (o) => isFieldFilled(o));
      const placeholdersFilled = !_.isEmpty(customPlaceholders);
      return baseFieldsFilled || placeholdersFilled;
    }

    // Additionally accept if at least one custom placeholder is set
    const placeholdersFilled = !_.isEmpty((offer as any).customPlaceholders);

    return allRequiredFilled || placeholdersFilled;
  })();

  const getStartingNodes = (nodes: Node[], edges: Edge[]) => {
    const targetNodes = edges.map((edge) => edge.target);
    return nodes.filter((node) => !targetNodes.includes(node.id));
  };

  const functionNodes = reactFlow?.nodes.filter((node) => node.type === NodeVariants.FUNCTION_NODE) || [];
  const conditionalNodes =
    reactFlow?.nodes.filter((node) => node.type === NodeVariants.CONDITIONAL_NODE || node.type === NodeVariants.CONDITION_WITH_DURATION_NODE) || [];
  const allNodesConnected = getStartingNodes(reactFlow?.nodes || [], reactFlow?.edges || []).length <= 1;
  const functionsSet = !_.isNil(reactFlow) && functionNodes.length > 0;
  const allMessagesHaveType = functionNodes.every((node) => node.data?.functionName);
  const allConditionsHaveType = conditionalNodes.every((node) => node.data?.functionName);
  const flowHasIssues = !allNodesConnected || !functionsSet || !allMessagesHaveType || !allConditionsHaveType;

  const workDaysSet = !_.isEmpty(workDays);
  const workHoursSet = !_.isEmpty(workHours);
  const linkedinAccountSelected = !_.isNil(linkedinAccount);
  const settingsHaveIssues = !linkedinAccountLoading && !linkedinAccountSelected;

  const campaignStepsArray = _.map(CampaignBuilderSteps, (tab) => tab);

  return (
    <Grid className={classes.campaignOverview} container={true} spacing={2}>
      <Grid
        size={{
          xs: 12,
          lg: 6,
        }}
      >
        <SummaryItem
          stepNumber={1}
          icon={<TextSnippetIcon className={classes.icon} />}
          title={t(campaignStepsArray.find((step) => step === CampaignBuilderSteps.CAMPAIGN_BRIEFING) || '')}
          hasError={!offerSet}
          selectCampaignStep={
            isCampaignRunning
              ? handleOpenStopRunningCampaignDialog(CampaignBuilderSteps.CAMPAIGN_BRIEFING)
              : selectCampaignStep(campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_BRIEFING))
          }
        >
          {offerSet ? (
            <Typography className={classes.blockText}>{_.truncate(getAiBriefingSummary(), { length: 700 })}</Typography>
          ) : (
            <Typography className={cx(classes.blockText, 'error')}>{t('offerNotSetError')}</Typography>
          )}
        </SummaryItem>
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 6,
        }}
      >
        <SummaryItem
          stepNumber={2}
          icon={<GroupIcon className={classes.icon} />}
          title={t(campaignStepsArray.find((step) => step === CampaignBuilderSteps.CAMPAIGN_CONTACT_LIST) || '')}
          selectCampaignStep={
            isCampaignRunning
              ? handleOpenStopRunningCampaignDialog(CampaignBuilderSteps.CAMPAIGN_CONTACT_LIST)
              : selectCampaignStep(campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_CONTACT_LIST))
          }
        >
          <Stack gap={2}>
            <Stack direction={'row'} gap={4}>
              <Typography className={classes.blockText}>
                <strong>{getLocalizedText('totalContactsInShortList', productGroup, t)}: </strong>
                {totalFinalContacts || 0}
              </Typography>
            </Stack>
            <div>
              {activeContactsSlice.map((contact, index) => (
                <SimpleContact key={index} contact={contact} />
              ))}
            </div>
            {activeContactsRest > 0 ? <Typography className={classes.boldText}>{`+ ${activeContactsRest} ${t('moreContacts')}`}</Typography> : null}
          </Stack>
        </SummaryItem>
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 6,
        }}
      >
        <SummaryItem
          stepNumber={3}
          icon={<AccountTreeIcon className={classes.icon} />}
          title={t(campaignStepsArray.find((step) => step === CampaignBuilderSteps.CAMPAIGN_FLOW) || '')}
          hasError={flowHasIssues}
          selectCampaignStep={
            isCampaignRunning
              ? handleOpenStopRunningCampaignDialog(CampaignBuilderSteps.CAMPAIGN_FLOW)
              : selectCampaignStep(campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_FLOW))
          }
        >
          <Stack gap={2}>
            <Stack gap={1}>
              {nodesSlice.map((node, index) => (
                <SimpleNodeItem key={index} node={node} />
              ))}
            </Stack>
            {nodesRest > 0 ? <Typography className={classes.boldText}>{`+ ${nodesRest} ${t('moreNodes')}`}</Typography> : null}
            {flowHasIssues ? (
              <div>
                <Typography className={cx(classes.blockText, 'error')} mb={'4px'}>
                  {t('flowHasIssues')}
                </Typography>
                {!allNodesConnected && (
                  <Typography className={cx(classes.blockText, 'error')} component={'li'}>
                    {t('notAllNodesConnected')}
                  </Typography>
                )}
                {!functionsSet && (
                  <Typography className={cx(classes.blockText, 'error')} component={'li'}>
                    {t('notAllFunctionsSet')}
                  </Typography>
                )}
                {!allMessagesHaveType && (
                  <Typography className={cx(classes.blockText, 'error')} component={'li'}>
                    {t('notAllMessagesHaveType')}
                  </Typography>
                )}
                {!allConditionsHaveType && (
                  <Typography className={cx(classes.blockText, 'error')} component={'li'}>
                    {t('notAllConditionsHaveType')}
                  </Typography>
                )}
              </div>
            ) : null}
          </Stack>
        </SummaryItem>
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 6,
        }}
      >
        <SummaryItem
          stepNumber={4}
          icon={<SettingsIcon className={classes.icon} />}
          title={t(campaignStepsArray.find((step) => step === CampaignBuilderSteps.CAMPAIGN_SETTINGS) || '')}
          hasError={settingsHaveIssues}
          selectCampaignStep={
            isCampaignRunning
              ? handleOpenStopRunningCampaignDialog(CampaignBuilderSteps.CAMPAIGN_SETTINGS)
              : selectCampaignStep(campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SETTINGS))
          }
        >
          <Stack gap={2}>
            <div className={cx(classes.settingItem, 'border')}>
              <Stack direction={'row'} gap={2}>
                <LinkedInIcon className={classes.settingIcon} />
                <div>
                  <Typography className={classes.blockText} mb={'8px'}>
                    {t('campaignMessagesFromLinkedIn')}
                  </Typography>
                  {linkedinAccountLoading ? (
                    <Typography className={classes.greyText}>{t('loading')}...</Typography>
                  ) : linkedinAccountSelected ? (
                    <div className={classes.setting}>{fullLinkedinAccount?.userEmail || t('none')}</div>
                  ) : (
                    <Typography className={cx(classes.blockText, 'error')}>{t('linkedinAccountNotSelected')}</Typography>
                  )}
                </div>
              </Stack>
            </div>
            <div className={cx(classes.settingItem, 'border')}>
              <Stack direction={'row'} gap={2}>
                <CalendarMonthIcon className={classes.settingIcon} />
                <div>
                  <Typography className={classes.blockText} mb={'8px'}>
                    {t('campaignOperatingDays')}
                  </Typography>
                  <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
                    {workDays?.map((day, index) => (
                      <div key={index} className={classes.setting}>
                        {t(day)}
                      </div>
                    ))}
                    {!workDaysSet && <Typography className={cx(classes.blockText, 'error')}>{t('workDaysNotSet')}</Typography>}
                  </Stack>
                  <Typography className={classes.greyText} mt={'8px'}>
                    {t('campaignAccountLimitsHint')}
                  </Typography>
                </div>
              </Stack>
            </div>
            <div className={classes.settingItem}>
              <Stack direction={'row'} gap={2}>
                <AccessTimeIcon className={classes.settingIcon} />
                <Stack direction={'row'} justifyContent={'space-between'} gap={4} sx={{ width: '100%' }}>
                  <div>
                    <Typography className={classes.blockText} mb={'8px'}>
                      {t('campaignOperatingHours')}
                    </Typography>
                    <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
                      {_.sortBy(workHours)?.map((hour, index) => (
                        <div key={index} className={classes.setting}>
                          {hour}
                        </div>
                      ))}
                      {!workHoursSet && <Typography className={cx(classes.blockText, 'error')}>{t('workHoursNotSet')}</Typography>}
                    </Stack>
                  </div>
                  <div>
                    <Typography className={classes.blockText} mb={'8px'}>
                      {t('timezone')}:
                    </Typography>
                    <div className={classes.setting}>{timeZone || t('none')}</div>
                  </div>
                </Stack>
              </Stack>
            </div>
          </Stack>
        </SummaryItem>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles()((theme) => ({
  campaignOverview: {},
  summaryItem: {
    background: '#FFF',
    borderRadius: '12px',
    height: '100%',
    '&.error': {
      border: `1px solid ${theme.palette.error.main}`,
      background: lighten(theme.palette.error.main, 0.95),
    },
  },
  itemHeader: {
    padding: '8px 24px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    '&.error': {
      borderBottom: `1px solid ${theme.palette.error.main}`,
    },
  },
  itemHeaderContent: {
    '&.error': {
      color: theme.palette.error.main,
      '& .MuiSvgIcon-root': {
        color: theme.palette.error.main,
      },
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(30),
    color: theme.palette.greys.medium,
  },
  boldText: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
  },
  editButton: {
    padding: '4px',
    color: theme.palette.greys.medium,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  editIcon: {
    fontSize: theme.typography.pxToRem(20),
  },
  itemBody: {
    overflow: 'auto',
    padding: '24px',
  },
  blockText: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
    '&.error': {
      color: theme.palette.error.main,
    },
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  settingItem: {
    '&.border': {
      paddingBottom: '24px',
      borderBottom: `1px solid ${theme.palette.greys.light}`,
    },
  },
  settingIcon: {
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.greys.medium,
  },
  setting: {
    background: theme.palette.backgrounds.main_new,
    padding: '4px 8px',
    borderRadius: '6px',
    width: 'fit-content',
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  simpleContact: {
    padding: '8px 4px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  contactName: {
    fontSize: theme.typography.pxToRem(13),
  },
  contactOccupation: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
}));

export default CampaignSummary;
